import { SDKEventMap, EventType, EventHandler } from './types';

/**
 * emit()'s arguments: none for a `void` event, exactly one payload otherwise.
 * Stops a typed event from being emitted without the data its handlers expect.
 */
type EmitArgs<K extends EventType> = SDKEventMap[K] extends void ? [] : [data: SDKEventMap[K]];

/**
 * Simple event emitter for SDK events, typed by SDKEventMap.
 */
export class EventEmitter {
  // Handlers of different events have different payload types; the map is
  // keyed by event so each set only ever sees its own payload.
  private handlers: Map<EventType, Set<(data: unknown) => void>> = new Map();

  /**
   * Register an event handler
   */
  on<K extends EventType>(event: K, handler: EventHandler<K>): void {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, new Set());
    }
    this.handlers.get(event)!.add(handler as (data: unknown) => void);
  }

  /**
   * Unregister an event handler
   */
  off<K extends EventType>(event: K, handler: EventHandler<K>): void {
    const handlers = this.handlers.get(event);
    if (handlers) {
      handlers.delete(handler as (data: unknown) => void);
    }
  }

  /**
   * Register a one-time event handler
   */
  once<K extends EventType>(event: K, handler: EventHandler<K>): void {
    const onceHandler: EventHandler<K> = (data) => {
      this.off(event, onceHandler);
      handler(data);
    };
    this.on(event, onceHandler);
  }

  /**
   * Emit an event
   */
  emit<K extends EventType>(event: K, ...args: EmitArgs<K>): void {
    const handlers = this.handlers.get(event);
    if (handlers) {
      // Copied first: a once() handler removes itself mid-iteration.
      [...handlers].forEach(handler => {
        try {
          handler(args[0]);
        } catch (error) {
          console.error(`Error in event handler for ${event}:`, error);
        }
      });
    }
  }

  /**
   * Remove all handlers for an event, or all handlers if no event specified
   */
  removeAllListeners(event?: EventType): void {
    if (event) {
      this.handlers.delete(event);
    } else {
      this.handlers.clear();
    }
  }
}
